import React, { useState, useRef, useEffect, useImperativeHandle } from 'react';
import { getSession } from '../../getSession';
import { getCustomUserTaskListByUserId, getUserFromHashedList } from '../../utils/lead';
import { Calendar, Clock } from 'lucide-react';

const sampleOwners = [
  'kmukesh343 (Mukesh Kumar)',
  '34594-Vikas.Rawat (Vikas Rawat)',
  '34594-Chintan.Gujrati (Chintan Gujrati)'
];

const sampleCollaborators = [
  '34594-Mohit.cheema (Mohit Cheema)',
  '34594-Manish.Singh (Surjit Kaur)',
  '34594-Rachit.Kumar (naseem khan)',
  '34594-Perumal.R (Perumal R)',
  '34594-kmukesh343 (Mukesh Kumar)'
];

const AddTask = React.forwardRef(({ onClose, onSubmit, initial = {} }, ref) => {
  const [title, setTitle] = useState(initial.title || `NewTask_${Date.now()}`);
  const [description, setDescription] = useState(initial.description || '');
  const [remarks, setRemarks] = useState(initial.remarks || '');
  const [activityTypes, setActivityTypes] = useState([]);
  const [activityType, setActivityType] = useState(initial.activityType || '');
  const [dueDateTime, setDueDateTime] = useState(initial.dueDateTime || '');
  const [owners, setOwners] = useState([]);
  const [owner, setOwner] = useState(initial.owner || '');

  // Collaborators multi-select
  const [collabQuery, setCollabQuery] = useState('');
  const [collaborators, setCollaborators] = useState(initial.collaborators || []);
  const [collaboratorOptions, setCollaboratorOptions] = useState([]);
  const [isUsersLoading, setIsUsersLoading] = useState(false);
  const [showCollabSuggestions, setShowCollabSuggestions] = useState(false);
  const collabRef = useRef(null);
  const pickerRef = useRef(null);
  const [showPicker, setShowPicker] = useState(false);

  const timeOptions = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      const hh = String(h).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      timeOptions.push(`${hh}:${mm}`);
    }
  }

  const splitDateTime = (val) => {
    const [datePart = '', timePart = ''] = String(val || '').split('T');
    return { datePart, timePart: timePart.slice(0, 5) };
  };

  const mergeDateTime = (datePart, timePart) => {
    if (!datePart) return '';
    return `${datePart}T${timePart || '00:00'}`;
  };

  const handleAddCollaborator = (c) => {
    // c is option object { value, label }
    if (!collaborators.some(x => x.value === c.value)) setCollaborators(prev => [...prev, c]);
    setCollabQuery('');
    setShowCollabSuggestions(false);
  };

  const handleRemoveCollaborator = (c) => {
    setCollaborators(prev => prev.filter(x => x.value !== c.value));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title || !dueDateTime) {
      alert('Please provide Title and Due Date and Time');
      return;
    }
    const payload = { title, description, remarks, activityType, dueDateTime, owner, collaborators };
    if (onSubmit) onSubmit(payload);
  };

  useImperativeHandle(ref, () => ({
    getFormData: () => {
      // normalize collaborators to array of ids
      const collabIds = collaborators.map(c => c.value || c);
      return {
        Title: title,
        Description: description,
        Remarks: remarks,
        TaskType: activityType,
        StartDate: dueDateTime || null,
        EndDate: dueDateTime || null,
        AssignedTo: owner || 0,
        Collaborators: collabIds,
        IsTask: true
      };
    }
  }));

  const formatToDisplay = (val) => {
    if (!val) return '';
    const [datePart, timePart] = String(val).split('T');
    if (!datePart) return val;
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour = '00', minute = '00'] = (timePart || '').split(':');
    if (!year || !month || !day) return val;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${String(day).padStart(2, '0')}-${months[month - 1]}-${year} ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  };

  useEffect(() => {
    const handleOutside = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) setShowPicker(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  useEffect(() => {
    let mounted = true;
    const fetchActivityTypes = async () => {
      try {
        const { userId, TokenId } = getSession();
        const payload = {
          Token: TokenId,
          Message: '',
          LoggedUserId: userId,
          MAC_Address: '',
          IP_Address: '',
          Details: { UserId: userId },
          BroadcastName: ''
        };
        const resp = await getCustomUserTaskListByUserId(payload);
        // resp is expected to be array of objects with TasKName or TaskName
        const items = Array.isArray(resp) ? resp : (resp?.Details && Array.isArray(resp.Details) ? resp.Details : []);
        const names = items.map(it => it?.TasKName ?? it?.TaskName ?? it?.TasKName ?? '').filter(Boolean);
        if (mounted && names.length) {
          setActivityTypes(names);
          // if no initial activityType provided, default to first
          if (!initial.activityType) setActivityType(names[0]);
        } else if (mounted && names.length === 0) {
          // fallback to some defaults if API returned nothing
          const fallback = ['Call', 'Email', 'Meeting', 'Demo'];
          setActivityTypes(fallback);
          if (!initial.activityType) setActivityType(fallback[0]);
        }
      } catch (err) {
        console.warn('Failed to load custom user task list', err?.message || err);
        if (mounted) {
          const fallback = ['Call', 'Email', 'Meeting', 'Demo'];
          setActivityTypes(fallback);
          if (!initial.activityType) setActivityType(fallback[0]);
        }
      }
    };
    fetchActivityTypes();
    return () => { mounted = false; };
  }, []);

  // Fetch users for Owner and Collaborators
  useEffect(() => {
    let mounted = true;
    const fetchUsers = async () => {
      setIsUsersLoading(true);
      try {
        const { userId, TokenId } = getSession();
        const payload = {
          Token: TokenId,
          Message: '',
          LoggedUserId: userId,
          MAC_Address: '',
          IP_Address: '',
          Details: { UserId: userId },
          BroadcastName: ''
        };
        const resp = await getUserFromHashedList(payload);
        let raw = resp?.Details ?? resp?.d ?? resp ?? [];
        if (raw && raw.data) raw = raw.data;
        if (raw && !Array.isArray(raw)) {
          raw = raw.ResultList ?? raw.Users ?? raw.UserList ?? raw.Data ?? raw.Items ?? raw;
        }
        const usersArray = Array.isArray(raw) ? raw : [];
        const mapToOption = (u) => {
          const id = u.Code ?? u.Id ?? u.ID ?? u.UserId ?? u.Value ?? u.ValueId ?? '';
          const label = u.Text ?? u.TextN ?? u.Name ?? u.FullName ?? u.DisplayName ?? u.LoginName ?? String(id);
          return { value: String(id), label: String(label) };
        };
        if (mounted) {
          const options = usersArray.map(mapToOption);
          setOwners(options);
          setCollaboratorOptions(options);
          if (!initial.owner && options.length) setOwner(options[0].value);
        }
      } catch (error) {
        console.error('fetch users error:', error);
      } finally {
        if (mounted) setIsUsersLoading(false);
      }
    };
    fetchUsers();
    return () => { mounted = false };
  }, []);

  return (
    <div className="w-full max-h-[80vh] overflow-y-auto p-4">
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">Title <span className="text-red-500">*</span></label>
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
            required
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">Description</label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
            placeholder="Start Typing the details about the task."
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">Remarks</label>
          <textarea
            value={remarks}
            onChange={e => setRemarks(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
            placeholder="about the task..."
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">Sales Activity Type</label>
          <select className="w-full px-3 py-2 border border-gray-300 rounded text-sm" value={activityType} onChange={e => setActivityType(e.target.value)}>
            {activityTypes.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>

        <div className="mb-4 relative" ref={pickerRef}>
          <label className="block text-sm font-medium text-gray-700">Due Date and Time <span className="text-red-500">*</span></label>
          <div className="relative mt-2">
            <input
              type="text"
              readOnly
              value={dueDateTime ? formatToDisplay(dueDateTime) : ''}
              onMouseDown={(e) => { e.preventDefault(); setShowPicker(prev => !prev); }}
              placeholder="Select date and time"
              className="w-full px-3 py-2 border border-gray-300 rounded bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <Calendar className="absolute right-3 top-2.5 w-5 h-5 text-gray-400 pointer-events-none" />
          </div>
          {showPicker && (
            <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-md shadow-xl p-4 flex flex-wrap gap-3">
              <div className="flex flex-col gap-1.5 flex-1 min-w-[140px]">
                <label className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Date</label>
                <input
                  type="date"
                  value={splitDateTime(dueDateTime).datePart}
                  onChange={(e) => setDueDateTime(mergeDateTime(e.target.value, splitDateTime(dueDateTime).timePart))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                />
              </div>
              <div className="flex flex-col gap-1.5 flex-1 min-w-[100px]">
                <label className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Time</label>
                <select
                  value={splitDateTime(dueDateTime).timePart}
                  onChange={(e) => setDueDateTime(mergeDateTime(splitDateTime(dueDateTime).datePart, e.target.value))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 appearance-none"
                  style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'%236b7280\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\' /%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.5rem center', backgroundSize: '1.25rem' }}
                >
                  {timeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="w-full flex justify-end mt-2 pt-2 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowPicker(false)}
                  className="px-4 py-1.5 text-sm font-medium bg-blue-600 text-white rounded hover:bg-blue-700 transition shadow-sm"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">Owner</label>
          <select className="w-full px-3 py-2 border border-gray-300 rounded text-sm" value={owner} onChange={e => setOwner(e.target.value)}>
            {owners.length ? owners.map(o => <option key={o.value} value={o.value}>{o.label}</option>) : <option value="">Loading...</option>}
          </select>
        </div>

        <div className="mb-4 relative" ref={collabRef}>
          <label className="block text-sm font-medium text-gray-700">Collaborators</label>

          <div className="flex flex-wrap gap-2 mb-2">
            {collaborators.map((c) => (
              <div key={c.value} className="flex items-center gap-2 bg-blue-50 px-2 py-1 rounded text-sm">
                <span>{c.label}</span>
                <button type="button" className="text-gray-600" onClick={() => handleRemoveCollaborator(c)}>×</button>
              </div>
            ))}
          </div>

          <input
            type="text"
            value={collabQuery}
            onChange={e => { setCollabQuery(e.target.value); setShowCollabSuggestions(true); }}
            onFocus={() => setShowCollabSuggestions(true)}
            onBlur={() => setTimeout(() => setShowCollabSuggestions(false), 150)}
            placeholder="---Select Collaborators---"
            className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
          />

          {showCollabSuggestions && (
            <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded shadow-lg z-40 max-h-40 overflow-auto">
              {collaboratorOptions.filter(u => u.label.toLowerCase().includes((collabQuery || '').toLowerCase()) && !collaborators.some(c => c.value === u.value)).map((u) => (
                <div key={u.value} onMouseDown={(e) => { e.preventDefault(); handleAddCollaborator(u); }} className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm">{u.label}</div>
              ))}
            </div>
          )}
        </div>

      </form>
    </div>
  );

});

export default AddTask;